/**
 * @lines98/client/components/gnome-detail — БОЛЬШАЯ карточка-деталь гнома (overlay):
 * крупный гном + уровень/прогресс + 3 бонуса (открыт/порог) + престиж + действие.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-14 «чтоб можно было проваливаться в
 * карточку, как у зверей»). Тап по герою карточки гнома (плашка «Гномы») открывает эту
 * деталь — ТОТ ЖЕ drill-down-механизм, что у зверей (`beast-detail`): native `<dialog>`
 * overlay (top-layer + ::backdrop + focus-trap + Esc), крупный герой + развёрнутая инфа,
 * закрытие по фону/Esc/×/«Закрыть». Это «ветка» того же приёма «деталь-overlay», что у
 * зверя; отличие наполнения: у зверя — кольцо 7 камней + вставка/сплав; у гнома — БЕЗ
 * камней (owner-lock «вокруг гномов камней нет»), вместо них — уровень/прогресс/3 бонуса/
 * престиж + кнопка действия.
 *
 * **Контролируемый компонент (server-truth, как beast-detail):** деталь НЕ ходит в сеть.
 * Экран (`gnomes-screen`) держит состояние (placeholder сейчас; server `gnomes` block —
 * GN-4), ставит модель через `setState`, деталь сообщает НАМЕРЕНИЕ (`onAction(color)` —
 * прокачать/престиж). UI-фаза: мутации — GN-4; кнопка — affordance-заглушка (экран
 * показывает информативный статус). Когда GN-4 даст route — экран делает мутацию + ресинк
 * + снова `setState` (один путь, как Мастерская).
 *
 * **Native `<dialog>` + анимация open/close opacity-only** (как beast-detail — owner-
 * playtest «ничего не двигается даже на пиксель»; transform сдвигал бы внеосевые элементы).
 * prefers-reduced-motion отключает (CSS). Каркас (спрайт + разметка) рисуется МГНОВЕННО из
 * `color`; `setState` дотягивает динамику (уровень/прогресс/бонусы/престиж).
 *
 * **Кнопка апгрейда — цена ВМЕСТО слова (§D тикета fe-playtest-round2, owner 2026-06-22):**
 * в апгрейд-состоянии (level < L20) слово «Прокачать»/«Улучшить» УБРАНО — на его месте цена
 * (золото всегда, осколки с L15) + `▲`-индикатор апгрейда. Это ПЕРЕНОС готового паттерна
 * карточки гнома (gnome-card.ts:392-411 — там цена уже заменяет слово + есть `▲`); деталь
 * делает ТО ЖЕ визуально (когерентность §8.8). На L20 — слово «Престиж» БЕЗ цены/без `▲`.
 *
 * SCOPE: рендер детали гнома + intent действия + open/close overlay. NOT IN SCOPE: сетевые
 * мутации (экран, GN-4), множители добычи (GN-5), цены (balance).
 *
 * Cross-references:
 *   - components/beast-detail.ts (карточка-деталь-сестра: тот же overlay-приём, кольцо камней).
 *   - lib/gnome-state.ts (модель + предикаты раскрытия бонусов — single source логики).
 *   - components/meta-icons.ts (`gnomeFullGlyph` / `gnomeGlyph` / `ICON_GOLD` / `ICON_SHARD`
 *     / `gemGlyph`); icon-button.ts (×); info-popover.ts («?»).
 *   - gnome-detail.css (co-located; размеры от --cell-size; раскладка portrait/landscape).
 *   - meta-gnomes.md §5.3 (Surface C/деталь), §2.2 (раскрытие), §2.4 (престиж).
 */

import './gnome-detail.css';

import {
  GNOME_GEM_UNLOCK,
  GNOME_MAX_LEVEL,
  GNOME_SHARDS_UNLOCK,
} from '@lines98/shared/config/meta';
import type { ColorIndex } from '@lines98/shared/types/game';

import {
  gnomeName,
  formatGnomeBonusPercent,
  gnomeBonusPercent,
  gnomeUpgradeCost,
  isGemUnlocked,
  isGoldUnlocked,
  isMaxLevel,
  isShardsUnlocked,
  levelProgress,
  type GnomeState,
} from '../lib/gnome-state.js';
import { t } from '../lib/i18n/index.js';
import { createIconButton, ICON_CLOSE } from './icon-button.js';
import { mountInfoPopover, type InfoPopoverHandle } from './info-popover.js';
import { createPrestigeBadge, type PrestigeBadgeHandle } from './meta-badge.js';
import { ICON_GOLD, ICON_SHARD, gemGlyph, gnomeFullGlyph } from './meta-icons.js';

// Описательные копии (desc / lockedDesc) и locked-заголовок (lockedName «???») —
// i18n-ключи, резолвятся `t()` per-mount (деталь пересоздаётся при открытии; экран
// ремонтируется на смену языка). Тексты — в каталоге game.gnomeDetail.*.

/**
 * Подпись категории бонуса (строка детали) — i18n, реюз ключей карточки гнома
 * (game.gnomeCard.bonusGold/Shards/Gem). Резолвится `t()` per-mount.
 */
function bonusLabel(kind: 'gold' | 'shards' | 'gem'): string {
  const key =
    kind === 'gold'
      ? 'game.gnomeCard.bonusGold'
      : kind === 'shards'
        ? 'game.gnomeCard.bonusShards'
        : 'game.gnomeCard.bonusGem';
  return t(key);
}

/** Состояние для рендера детали (экран ставит из placeholder/server GN-4). */
export interface GnomeDetailState {
  /** Уровень 1..GNOME_MAX_LEVEL. */
  readonly level: number;
  /** Число престижей (≥0). */
  readonly prestigeCount: number;
  /**
   * Открыт ли гном (GN-2b, rev 0.3). `false` → locked-teaser-ветка: силуэт + teaser-текст
   * + «следующий гном на уровне N», БЕЗ кнопки действия (AC-7/AC-4). По умолчанию `true`
   * (back-compat для уже-открытых вызовов). Один overlay, две ветки (AC-7).
   */
  readonly unlocked?: boolean;
  /**
   * Уровень следующей вехи (server-truth) для teaser-строки «следующий гном на уровне N»
   * (AC-3). Только для locked-ветки; `null`/опущен → строку не показываем. NO-HARDCODE.
   */
  readonly nextUnlockLevel?: number | null;
  /**
   * Баланс игрока (золото + осколки) — КОНТРОЛИРУЕМЫЙ prop от экрана (П12 AC-12.4): деталь
   * НЕ ходит в сеть, экран передаёт текущий баланс. По нему dim/disable кнопки «Прокачать»
   * при нехватке. Опущен → не дизейблим по балансу (affordance остаётся как раньше).
   */
  readonly balance?: { readonly gold: number; readonly shards: number };
}

export interface GnomeDetailOptions {
  /** Цвет гнома 1..7 — задаёт статичный каркас (спрайт + разметка) сразу. */
  readonly color: ColorIndex;
  /**
   * Намерение действия (прокачать / престиж на L20). Экран (GN-4) делает мутацию + ресинк
   * + `setState`. UI-фаза: экран показывает информативный статус.
   */
  readonly onAction?: (color: ColorIndex) => void;
  /** Колбэк закрытия overlay (экран снимает деталь). */
  readonly onClose?: () => void;
}

export interface GnomeDetailHandle {
  readonly element: HTMLDialogElement;
  /** Открыть overlay (showModal). */
  open(): void;
  /** Закрыть overlay. */
  close(): void;
  /** Поставить состояние (уровень/престиж) — динамика без пересборки каркаса. */
  setState(state: GnomeDetailState): void;
  /** Заблокировать кнопку действия на время полёта мутации (pending, GN-4). */
  setPending(pending: boolean): void;
  /** Размонтировать: снять overlay + слушатели. */
  unmount(): void;
}

interface BonusRow {
  readonly kind: 'gold' | 'shards' | 'gem';
  readonly root: HTMLElement;
  readonly statusEl: HTMLElement;
}

/**
 * Смонтировать большую карточку-деталь гнома как overlay. Каркас (спрайт + разметка)
 * рисуется сразу из `color`; `setState` дотягивает динамику. Возвращает handle.
 */
export function mountGnomeDetail(opts: GnomeDetailOptions): GnomeDetailHandle {
  const { color, onAction, onClose } = opts;
  const name = gnomeName(color);

  let state: GnomeDetailState | null = null;
  let pending = false;
  let unmounted = false;

  // ---- Native dialog overlay ----
  const dialog = document.createElement('dialog');
  dialog.className = 'gnome-detail';
  dialog.dataset.color = String(color);
  dialog.setAttribute('aria-label', t('game.gnomeDetail.gnomeAria', { name }));

  // ── Шапка: заголовок + «?» (touch) + ×. ──
  const header = document.createElement('header');
  header.className = 'gnome-detail-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'gnome-detail-title';
  titleEl.textContent = name;
  const closeBtn = createIconButton({
    glyph: ICON_CLOSE,
    label: t('game.gnomeDetail.close'),
    variant: 'ghost',
    className: 'gnome-detail-close',
  }).element;
  header.append(titleEl, closeBtn);

  // ── Stage: крупный гном-герой (full-арт, БЕЗ камней вокруг — owner-lock §5.3). ──
  const stage = document.createElement('div');
  stage.className = 'gnome-detail-stage';
  const hero = document.createElement('div');
  hero.className = 'gnome-detail-hero';
  hero.setAttribute('aria-hidden', 'true');
  hero.innerHTML = gnomeFullGlyph(color); // статичный full-арт — рисуется сразу
  hero.style.setProperty('--gnome-tint', `var(--ball-${color})`);
  // Престиж-бейдж поверх героя — ПРИМИТИВ зверя (`createPrestigeBadge`, гранёный самоцвет +
  // число, БЕЗ КАПА; owner-directive 2026-06-14 «престиж — брать из зверей»). Скрыт при 0.
  const prestigeBadge: PrestigeBadgeHandle = createPrestigeBadge({
    className: 'gnome-detail-prestige',
    ariaPrefix: t('game.gnomeDetail.prestigeCount'),
  });
  prestigeBadge.element.hidden = true;
  hero.appendChild(prestigeBadge.element);
  stage.appendChild(hero);

  // ── Side: уровень+прогресс / 3 бонуса / описание / кнопка («?»/Закрыть). ──
  const side = document.createElement('div');
  side.className = 'gnome-detail-side';

  // Уровень `L / 20` + полоса прогресса.
  const levelRow = document.createElement('div');
  levelRow.className = 'gnome-detail-level-row';
  const levelEl = document.createElement('span');
  levelEl.className = 'gnome-detail-level';
  levelRow.appendChild(levelEl);
  const progress = document.createElement('div');
  progress.className = 'gnome-detail-progress';
  progress.setAttribute('role', 'progressbar');
  progress.setAttribute('aria-valuemin', '1');
  progress.setAttribute('aria-valuemax', String(GNOME_MAX_LEVEL));
  const progressFill = document.createElement('span');
  progressFill.className = 'gnome-detail-progress-fill';
  progress.appendChild(progressFill);

  // 3 бонуса (золото L1 / осколки L5 / камень L15) — строки [иконка + подпись + статус].
  const bonuses = document.createElement('div');
  bonuses.className = 'gnome-detail-bonuses';
  const goldRow = buildBonusRow('gold', ICON_GOLD);
  const shardsRow = buildBonusRow('shards', ICON_SHARD);
  const gemRow = buildBonusRow('gem', gemGlyph(color));
  bonuses.append(goldRow.root, shardsRow.root, gemRow.root);

  // Описание — inline (видно на ПК через CSS, спрятано на touch — там за «?»).
  const descEl = document.createElement('p');
  descEl.className = 'gnome-detail-desc';
  descEl.textContent = t('game.gnomeDetail.desc', { max: GNOME_MAX_LEVEL });

  // Кнопка действия («Прокачать» / «Престиж»). Текст-лейбл — отдельный узел, чтобы цена
  // (П12) рисовалась рядом, а не конкатенацией в textContent (управляем видимостью отдельно).
  const actionBtn = document.createElement('button');
  actionBtn.type = 'button';
  actionBtn.className = 'btn gnome-detail-action';
  const actionLabel = document.createElement('span');
  actionLabel.className = 'gnome-detail-action-label';
  // Цена L→L+1 (П12): золото всегда, осколки с L15. Иконки валют — примитивы ICON_GOLD/
  // ICON_SHARD (meta-icons). Числа — из config через gnomeUpgradeCost (no-hardcode). Узлы
  // создаём один раз, applyState проставляет числа / прячет на L20 (без цены) / золото без осколков.
  const actionCost = document.createElement('span');
  actionCost.className = 'gnome-detail-action-cost';
  actionCost.setAttribute('aria-hidden', 'true'); // смысл цены — в aria-label кнопки (AC-12.7)
  const costGold = document.createElement('span');
  costGold.className = 'gnome-detail-action-cost--gold';
  const costGoldNum = document.createElement('span');
  costGoldNum.className = 'gnome-detail-action-cost-num';
  costGold.innerHTML = ICON_GOLD; // иконка-примитив (img); число добавляем узлом ниже
  costGold.appendChild(costGoldNum);
  const costShards = document.createElement('span');
  costShards.className = 'gnome-detail-action-cost--shards';
  const costShardsNum = document.createElement('span');
  costShardsNum.className = 'gnome-detail-action-cost-num';
  costShards.innerHTML = ICON_SHARD;
  costShards.appendChild(costShardsNum);
  actionCost.append(costGold, costShards);
  // `▲`-индикатор апгрейда (§D AC-D.2): когерентность с карточкой гнома (gnome-card.ts:235-238
  // `.gnome-card-arrow` `▲`), знак «улучшить» (level-up). Декоративный (aria-hidden) — смысл
  // апгрейда несёт цена + aria-label кнопки. Тонкий текст-глиф рядом с иконками валют (§10.3).
  // В DOM ТОЛЬКО в апгрейд-состоянии (level < L20); на L20 (Престиж) удаляется (нет апгрейда).
  const actionArrow = document.createElement('span');
  actionArrow.className = 'gnome-detail-action-arrow';
  actionArrow.setAttribute('aria-hidden', 'true');
  actionArrow.textContent = '▲';
  // Порядок в кнопке: лейбл (пуст в апгрейде — слово убрано §D AC-D.1) → цена → `▲` (как
  // карточка: cost · label · arrow). В апгрейде видна только цена + `▲`; на L20 — слово «Престиж».
  actionBtn.append(actionLabel, actionCost, actionArrow);
  actionBtn.addEventListener('click', () => {
    if (pending) return;
    if (actionBtn.disabled) return; // нехватка валюты / pending → no-op (AC-12.4)
    onAction?.(color);
  });

  const footer = document.createElement('div');
  footer.className = 'gnome-detail-footer';
  const doneBtn = document.createElement('button');
  doneBtn.type = 'button';
  doneBtn.className = 'btn gnome-detail-done';
  doneBtn.textContent = t('game.gnomeDetail.close');
  // actionBtn добавляется в footer ТОЛЬКО для открытого гнома (locked → его нет в DOM,
  // AC-4/AC-7 — `.gnome-detail-action` count 0). doneBtn («Закрыть») есть всегда.
  footer.append(actionBtn, doneBtn);

  // LOCKED-teaser-блок (GN-2b, AC-7): силуэт (уже в hero) + teaser-текст + «следующий гном
  // на уровне N». Заменяет уровень/прогресс/бонусы/кнопку, когда гном не открыт. Текст
  // fluid (CSS §8.10). В DOM только в locked-ветке (toggle в applyState).
  const teaser = document.createElement('div');
  teaser.className = 'gnome-detail-teaser';
  const teaserText = document.createElement('p');
  teaserText.className = 'gnome-detail-teaser-text';
  teaserText.textContent = t('game.gnomeDetail.lockedDesc'); // общий «как открыть» без имени/цвета (single source)
  const teaserNext = document.createElement('p');
  teaserNext.className = 'gnome-detail-teaser-next';
  teaser.append(teaserText, teaserNext);

  side.append(levelRow, progress, bonuses, descEl, teaser, footer);

  // «?» возле заголовка (на touch) — описание; используем info-popover (shared, как Мастерская).
  const titleInfoWrap = document.createElement('span');
  titleInfoWrap.className = 'gnome-detail-title-info';
  titleEl.after(titleInfoWrap);

  dialog.append(header, stage, side);
  document.body.appendChild(dialog);

  const infoPopover: InfoPopoverHandle = mountInfoPopover({
    title: name,
    bodyText: t('game.gnomeDetail.desc', { max: GNOME_MAX_LEVEL }),
    triggerHost: titleInfoWrap,
    variant: '?',
  });

  /** Применить состояние (уровень/прогресс/бонусы/престиж/кнопка) → DOM. */
  function applyState(s: GnomeDetailState): void {
    state = s;
    const { level, prestigeCount } = s;

    // --- LOCKED-teaser-ветка (GN-2b, AC-7/AC-4): силуэт + teaser-текст + «следующий гном
    //     на уровне N», БЕЗ кнопки прокачки/престижа. Один overlay, две ветки. ---
    if (s.unlocked === false) {
      dialog.classList.add('gnome-detail--locked');
      // СЕКРЕТ (AC-3 / §2.0 / §5.1): имя гнома = его ЦВЕТ → НЕ показываем. Заголовок → «???»,
      // aria-label overlay'а — нейтральный (без имени). Цвет даёт ТОЛЬКО силуэт (CSS-filter).
      // «?»-popover (его заголовок = имя) на locked-слоте СКРЫТ — пояснение «как открыть» уже
      // в teaser-тексте inline на всех viewport; иначе popover-title раскрыл бы имя/цвет.
      titleEl.textContent = t('game.gnomeDetail.lockedName');
      dialog.setAttribute('aria-label', t('game.gnomeDetail.lockedTeaser'));
      titleInfoWrap.hidden = true;
      // Открытые секции (уровень/прогресс/бонусы/описание) — прочь; teaser — на сцену.
      levelRow.hidden = true;
      progress.hidden = true;
      bonuses.hidden = true;
      descEl.hidden = true;
      teaser.hidden = false;
      prestigeBadge.element.hidden = true;
      // Кнопка действия — ИЗ DOM (AC-4 `.gnome-detail-action` count 0). doneBtn остаётся.
      if (actionBtn.parentNode) actionBtn.parentNode.removeChild(actionBtn);
      // Строка «следующий гном на уровне N» — server-truth (NO-HARDCODE, AC-3).
      const next = s.nextUnlockLevel;
      if (typeof next === 'number' && Number.isFinite(next)) {
        teaserNext.textContent = t('game.gnomes.nextUnlock', { n: next });
        teaserNext.hidden = false;
      } else {
        teaserNext.textContent = '';
        teaserNext.hidden = true;
      }
      return;
    }

    // Открытая ветка: вернуть имя/aria/«?», секции, спрятать teaser, вернуть кнопку в footer.
    dialog.classList.remove('gnome-detail--locked');
    titleEl.textContent = name;
    dialog.setAttribute('aria-label', t('game.gnomeDetail.gnomeAria', { name }));
    titleInfoWrap.hidden = false;
    levelRow.hidden = false;
    progress.hidden = false;
    bonuses.hidden = false;
    descEl.hidden = false;
    teaser.hidden = true;
    if (!actionBtn.parentNode) footer.insertBefore(actionBtn, doneBtn);

    const max = isMaxLevel(level);

    levelEl.textContent = t('game.gnomeDetail.level', { n: level, max: GNOME_MAX_LEVEL });
    levelEl.classList.toggle('gnome-detail-level--max', max);

    const p = levelProgress(level);
    progressFill.style.width = `${Math.round(p * 100)}%`;
    progress.setAttribute('aria-valuenow', String(level));

    // Престиж — ПРИМИТИВ зверя (число престижей, БЕЗ КАПА). Скрыт при 0.
    const hasPrestige = prestigeCount > 0;
    prestigeBadge.element.hidden = !hasPrestige;
    if (hasPrestige) prestigeBadge.set(prestigeCount);

    // Растущее значение бонуса для ОТКРЫТОЙ категории = серверная истина (AC-6/AC-8):
    // тот же `gnomeBonusPercent` (shared `gnomeContribution`), что зовёт карточка, и тот же
    // формат-хелпер `formatGnomeBonusPercent` → строка детали == чип карточки. Закрытая
    // категория → null → строка показывает порог «Откроется на L?» (AC-5).
    const bonusState = { color, level, prestigeCount, unlocked: true };
    applyBonusRow(goldRow, isGoldUnlocked(level), 0, bonusState);
    applyBonusRow(shardsRow, isShardsUnlocked(level), GNOME_SHARDS_UNLOCK, bonusState);
    applyBonusRow(gemRow, isGemUnlocked(level), GNOME_GEM_UNLOCK, bonusState);

    // СЛОВО на кнопке (§D AC-D.1): в апгрейд-состоянии слово «Прокачать»/«Улучшить» УБРАНО —
    // на его месте стоит ЦЕНА (см. ниже). Слово остаётся ТОЛЬКО на L20 = «Престиж» (это НЕ
    // апгрейд-состояние). Перенос готового паттерна карточки (gnome-card.ts:392-411 — там
    // в апгрейде `actionLabel.textContent=''`, рисуется только цена). Когерентность §8.8.
    actionLabel.textContent = max ? t('game.gnomeDetail.prestige') : '';
    actionBtn.classList.toggle('gnome-detail-action--prestige', max);
    actionBtn.classList.toggle('primary', !max);

    // --- Цена L→L+1 + `▲`-индикатор на кнопке (§D, перенос паттерна карточки) ---
    // На L20 (max) апгрейда нет → НЕТ цены И НЕТ `▲` (оба узла из DOM, AC-D.4 count 0 — как
    // карточка прячет arrow на максимуме). Иначе (апгрейд) — золото всегда; осколки ТОЛЬКО
    // когда config-стоимость осколков > 0 (с L15, AC-D.1/D.5: до L15 узла осколков нет, не
    // «0 осколков»); `▲` присутствует (AC-D.2). Числа — из config через gnomeUpgradeCost
    // (no-hardcode, AC-D.5).
    const cost = max ? { gold: 0, shards: 0 } : gnomeUpgradeCost(level);
    if (max) {
      if (actionCost.parentNode) actionCost.parentNode.removeChild(actionCost);
      if (actionArrow.parentNode) actionArrow.parentNode.removeChild(actionArrow);
    } else {
      if (!actionCost.parentNode) actionBtn.appendChild(actionCost);
      // `▲` всегда ПОСЛЕ цены в апгрейде (порядок cost · arrow, как карточка).
      if (!actionArrow.parentNode) actionBtn.appendChild(actionArrow);
      costGoldNum.textContent = String(cost.gold);
      const showShards = cost.shards > 0;
      costShards.hidden = !showShards;
      if (showShards) costShardsNum.textContent = String(cost.shards);
      else if (costShards.parentNode) costShards.parentNode.removeChild(costShards);
      if (showShards && !costShards.parentNode) actionCost.appendChild(costShards);
    }

    // Нехватка валюты (AC-12.4): кнопка приглушена/disabled (affordance, мутаций пока нет —
    // GN-4). Баланс — контролируемый prop от экрана; опущен → по балансу НЕ дизейблим.
    const bal = s.balance;
    const affordable = max || !bal || (bal.gold >= cost.gold && bal.shards >= cost.shards);
    actionBtn.classList.toggle('gnome-detail-action--unaffordable', !max && !affordable);

    // aria-label несёт цену (AC-12.7): смысл цены не только визуальный. Иконки декоративны.
    actionBtn.setAttribute(
      'aria-label',
      max
        ? t('game.gnomeDetail.prestigeAria', { name })
        : t('game.gnomeDetail.upgradeAria', { name, level: level + 1, gold: cost.gold }) +
          (cost.shards > 0 ? t('game.gnomeDetail.upgradeAriaShards', { shards: cost.shards }) : ''),
    );
    actionBtn.setAttribute('aria-disabled', !affordable || pending ? 'true' : 'false');
    actionBtn.disabled = pending || !affordable;
  }

  // ---- Закрытие overlay ----
  const closeOverlay = (): void => {
    if (unmounted) return;
    if (dialog.open) dialog.close();
  };
  closeBtn.addEventListener('click', closeOverlay);
  doneBtn.addEventListener('click', closeOverlay);
  const onDialogClick = (e: MouseEvent): void => {
    if (e.target === dialog) closeOverlay();
  };
  dialog.addEventListener('click', onDialogClick);
  const onCancel = (e: Event): void => {
    e.preventDefault();
    closeOverlay();
  };
  dialog.addEventListener('cancel', onCancel);
  const onClosed = (): void => {
    if (unmounted) return;
    if (onClose) onClose();
  };
  dialog.addEventListener('close', onClosed);

  return {
    element: dialog,
    open(): void {
      if (unmounted) return;
      dialog.showModal();
    },
    close: closeOverlay,
    setState(s: GnomeDetailState): void {
      applyState(s);
    },
    setPending(p: boolean): void {
      pending = p;
      dialog.classList.toggle('gnome-detail--pending', p);
      actionBtn.disabled = p;
      if (state) applyState(state);
    },
    unmount(): void {
      if (unmounted) return;
      unmounted = true;
      closeBtn.removeEventListener('click', closeOverlay);
      doneBtn.removeEventListener('click', closeOverlay);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('close', onClosed);
      infoPopover.unmount();
      if (dialog.open) dialog.close();
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    },
  };
}

// =============================================================================
// Bonus row helpers (строка детали: иконка + подпись + статус открыт/порог)
// =============================================================================

/** Построить строку бонуса [иконка + подпись + статус]. */
function buildBonusRow(kind: 'gold' | 'shards' | 'gem', iconHtml: string): BonusRow {
  const root = document.createElement('div');
  root.className = `gnome-detail-bonus gnome-detail-bonus--${kind}`;
  const icon = document.createElement('span');
  icon.className = 'gnome-detail-bonus-ic';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = iconHtml;
  const label = document.createElement('span');
  label.className = 'gnome-detail-bonus-label';
  label.textContent = bonusLabel(kind);
  const status = document.createElement('span');
  status.className = 'gnome-detail-bonus-status';
  root.append(icon, label, status);
  return { kind, root, statusEl: status };
}

/**
 * Применить открытость бонуса к строке. **Открыт → растущее значение «+X.X%»** (AC-6: не
 * просто «Открыто», а число, совпадающее с серверной добычей и с чипом карточки, AC-8).
 * Закрыт → приглушён + «Откроется на L?» (§6/AC-5: не «0» без контекста, а порог).
 * `unlockLevel` 0 → золото с L1. Статус-узел ПОСТОЯНЕН — меняется только textContent (AC-9).
 *
 * @param row — строка бонуса (kind = ресурс).
 * @param unlocked — открыта ли категория на текущем уровне.
 * @param unlockLevel — порог открытия (для метки закрытой категории).
 * @param state — состояние гнома (для расчёта растущего значения открытой категории).
 */
function applyBonusRow(
  row: BonusRow,
  unlocked: boolean,
  unlockLevel: number,
  state: GnomeState,
): void {
  row.root.classList.toggle('gnome-detail-bonus--locked', !unlocked);
  if (unlocked) {
    // Растущее значение = серверная истина (единый `gnomeBonusPercent` + формат-хелпер).
    const pct = gnomeBonusPercent(row.kind, state);
    const display = pct === null ? t('game.gnomeDetail.bonusOpen') : formatGnomeBonusPercent(pct);
    row.statusEl.textContent = display;
    row.statusEl.classList.add('gnome-detail-bonus-status--open');
    row.root.setAttribute(
      'aria-label',
      t('game.gnomeDetail.bonusAria', { label: bonusLabel(row.kind), value: display }),
    );
  } else {
    row.statusEl.textContent = t('game.gnomeDetail.lockedStatus', { n: unlockLevel });
    row.statusEl.classList.remove('gnome-detail-bonus-status--open');
    row.root.setAttribute(
      'aria-label',
      t('game.gnomeDetail.bonusLockedAria', { label: bonusLabel(row.kind), level: unlockLevel }),
    );
  }
}
